import React, { FC } from 'react';

import {
  Text,
  Flex,
  Modal,
  Center,
  Stack,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  CircularProgress,
} from '@chakra-ui/react';

import { useSKU } from 'hooks/useSKU';

import { Form, Formik } from 'formik';

import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';

import { selectors } from 'store/slices/washhub';

import { initialValues, validationSchema } from './AddManualItemModal.utils';

import { AddItemModalViewProps } from './AddManualItemModal.props';
import getSortedData from 'utils/getSortedData';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const AddItemModalView: FC<AddItemModalViewProps> = (props) => {
  const washHub = selectors.useSelectedWashHub();

  const { isOpen, onClose: _onClose, onSubmit, isLoading } = props;

  const { data: skus = [], isLoading: isLoadingSKU } = useSKU(
    'item',
    washHub?._id,
  );

  const onClose = () => {
    if (isLoading) {
      return;
    }

    _onClose();
  };

  return (
    <Modal
      size="2xl"
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px" position="relative" overflow="hidden">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader textAlign="center" fontWeight="500">
          Add new item
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" px={6} pb={6}>
            <Formik
              onSubmit={onSubmit}
              initialValues={initialValues}
              validationSchema={validationSchema}>
              {({ isValid }) => (
                <Form>
                  <Center>
                    <Stack minW="400px" spacing={6}>
                      <FormikSelectInput
                        placeholder="Select"
                        label="SKU"
                        isLoading={isLoadingSKU}
                        containerProps={{ flex: 1 }}
                        name="sku"
                        labelProps={labelProps}>
                        {getSortedData(skus)?.map((s) => (
                          <option key={s._id} value={s._id}>
                            {s?.name || ''}
                          </option>
                        ))}
                      </FormikSelectInput>
                      <FormikInput
                        name="washHub"
                        label="Wash Hub"
                        labelProps={labelProps}
                        isReadOnly
                        value={washHub?.name || ''}
                      />
                      <FormikInput
                        type="number"
                        name="quantity"
                        label="Quantity"
                        labelProps={labelProps}
                        containerProps={{ flex: 1 }}
                      />
                    </Stack>
                  </Center>
                  <Flex my={12} flexDirection="row" justifyContent="center">
                    <Button
                      variant="outline"
                      w="134px"
                      marginRight="10px"
                      onClick={onClose}
                      isDisabled={isLoading}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      w="134px"
                      isLoading={isLoading}
                      isDisabled={!isValid}>
                      Create
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
        {isLoading && (
          <Center
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="#00000030"
            flexDirection="column">
            <Flex
              direction="column"
              bg="#fff"
              align="center"
              p={5}
              borderRadius="20px"
              whiteSpace="pre-line">
              <Text textAlign="center" fontSize="22px">
                Creating items please wait...
              </Text>
              <CircularProgress mt={4} isIndeterminate color="primary.300" />
            </Flex>
          </Center>
        )}
      </ModalContent>
    </Modal>
  );
};

export default AddItemModalView;
